/**
 * BPS Color Palette & Design Tokens
 *
 * Comprehensive token-based color system following BPS brand guidelines.
 * All colors in the application derive from these tokens.
 *
 * Best Practices:
 * - Use semantic color names (e.g., ERROR_LIGHT, ERROR_DARK) over generic names
 * - CSS variables enable runtime theme switching
 * - Light variants used for light mode, Dark variants used for dark mode
 */

import { alpha } from '@mui/material/styles';

// Base Colors
export const WHITE = '#FFFFFF';
export const BLACK = '#000000';

// BPS Brand Colors (from style guide)
export const BPS_BLACK = '#101820';
export const BPS_DARK_GREY = '#4B4F54';
export const BPS_GREY = '#75787B';
export const BPS_LIGHT_GREY = '#C8C9C7';
export const BPS_DARK_BLUE = '#002554';
export const BPS_BLUE = '#233C78';
export const BPS_RED = '#E4002B';

// Accent Opacity
export const ACCENT_OPAQUE_LIGHT = alpha(BPS_BLUE, 0.24);
export const ACCENT_OPAQUE_DARK = alpha(BPS_LIGHT_GREY, 0.26);

// Editor Colors
export const EDITOR_BACKGROUND_LIGHT = '#F5F6F7';
export const EDITOR_LINE_NUMBER_PASSIVE_LIGHT = BPS_DARK_GREY;
export const EDITOR_LINE_NUMBER_ACTIVE_LIGHT = BPS_BLUE;
export const EDITOR_LINE_HIGHLIGHT_LIGHT = BPS_GREY;
export const EDITOR_TAB_BACKGROUND_LIGHT = alpha(BPS_BLUE, 0.375);

export const EDITOR_BACKGROUND_DARK = '#1D242A';
export const EDITOR_LINE_NUMBER_PASSIVE_DARK = BPS_LIGHT_GREY;
export const EDITOR_LINE_NUMBER_ACTIVE_DARK = WHITE;
export const EDITOR_LINE_HIGHLIGHT_DARK = '#3a3a3a';
export const EDITOR_TAB_BACKGROUND_DARK = alpha(BPS_LIGHT_GREY, 0.4);

// Console Colors
export const CONSOLE_BACKGROUND_LIGHT = '#F5F6F7';
export const CONSOLE_BACKGROUND_DARK = '#1D242A';

// Semantic Colors - Light/Dark variants only
// Light variant used for light mode, Dark variant used for dark mode
export const ERROR_LIGHT = '#FF3356';
export const ERROR_DARK = '#C40023';

export const WARNING_LIGHT = '#FF9800';
export const WARNING_DARK = '#BF360C';
/** Warning message text — higher contrast on dialog/paper backgrounds (BPS-529). */
export const WARNING_TEXT_LIGHT = '#8A2C0A';
export const WARNING_TEXT_DARK = '#FFD180';

export const INFO_LIGHT = BPS_BLUE;
export const INFO_DARK = BPS_LIGHT_GREY;

export const SUCCESS_LIGHT = '#4CAF50';
export const SUCCESS_DARK = '#1B5E20';

export const NEUTRAL_LIGHT = BPS_LIGHT_GREY;
export const NEUTRAL_DARK = BPS_DARK_GREY;

// Interaction States - WCAG AA compliant (use blue, not red)
export const FOCUS_RING = BPS_BLUE;
export const FOCUS_RING_OPACITY = alpha(FOCUS_RING, 0.4);

export const ACTION_HOVER = alpha(BPS_BLUE, 0.08);
export const ACTION_SELECTED = alpha(BPS_BLUE, 0.16);
export const ACTION_FOCUS = alpha(BPS_BLUE, 0.24);
export const ACTION_ACTIVE = alpha(BPS_BLUE, 0.56);

// WCAG AA Compliant Interactive Colors
export const INTERACTIVE_MAIN_LIGHT = BPS_BLUE;
export const INTERACTIVE_MAIN_DARK = WHITE;
export const TEXT_INTERACTIVE_LIGHT = BPS_BLUE;
export const TEXT_INTERACTIVE_DARK = WHITE;

// Text & Dividers
export const TEXT_PRIMARY_LIGHT = 'rgba(0, 0, 0, 0.87)';
export const TEXT_SECONDARY_LIGHT = 'rgba(0, 0, 0, 0.60)';
export const TEXT_DISABLED_LIGHT = 'rgba(0, 0, 0, 0.38)';

export const TEXT_PRIMARY_DARK = WHITE;
export const TEXT_SECONDARY_DARK = 'rgba(255, 255, 255, 0.70)';
export const TEXT_DISABLED_DARK = 'rgba(255, 255, 255, 0.38)';

export const DIVIDER_LIGHT = 'rgba(0, 0, 0, 0.12)';
export const DIVIDER_DARK = 'rgba(255, 255, 255, 0.12)';

// Gauge (dashboard velocity widget) — base stroke/fill aligned with MUI gauge defaults
export const GAUGE_BASE_COLOR = '#353A41';

// Chart Colors — each must meet WCAG 4.5:1 contrast with BPS_BLACK (chart series toggle labels).
export const CHART_BLUE = '#00add8';
export const CHART_GREEN = SUCCESS_LIGHT;
export const CHART_RED = '#f25e6d';
export const CHART_YELLOW = '#efbc2f';
/** Lightened from #b24599 so BPS_BLACK label text meets 4.5:1 on series toggle buttons. */
export const CHART_PURPLE = '#c16aad';
export const CHART_ORANGE = '#f47522';
export const CHART_TEAL = '#2ec4b6';
export const CHART_PINK = '#f284b8';

/** Up to eight motor/chart series; order is the assignment cycle in `sensor-utils`. */
export const CHART_COLOR_CYCLE = [CHART_BLUE, CHART_GREEN, CHART_YELLOW, CHART_RED, CHART_PURPLE, CHART_ORANGE, CHART_TEAL, CHART_PINK] as const;

// Rapid Robot accents match the robot families in the redesigned page mock.
export const RAPID_ROBOT_FLUX = CHART_TEAL;
export const RAPID_ROBOT_NOVA = CHART_PURPLE;
export const RAPID_ROBOT_MACH = CHART_BLUE;
export const RAPID_ROBOT_PIVOT = CHART_RED;
export const RAPID_ROBOT_DELTA = CHART_YELLOW;
export const RAPID_ROBOT_APEX = CHART_ORANGE;
export const RAPID_ROBOT_PILL_TEXT = BPS_BLACK;
export const RAPID_ROBOT_CARD_BACKGROUND_LIGHT = WHITE;
export const RAPID_ROBOT_CARD_BACKGROUND_DARK = EDITOR_BACKGROUND_DARK;
export const RAPID_ROBOT_CARD_TEXT_LIGHT = BPS_BLUE;
export const RAPID_ROBOT_CARD_TEXT_DARK = WHITE;
export const RAPID_ROBOT_CARD_CHIP_BACKGROUND_LIGHT = alpha(BPS_BLUE, 0.12);
export const RAPID_ROBOT_CARD_CHIP_BACKGROUND_DARK = alpha(WHITE, 0.16);
export const RAPID_ROBOT_CARD_BUTTON_TEXT = BPS_BLUE;
export const RAPID_ROBOT_NOTICE_BACKGROUND_LIGHT = alpha(BPS_BLUE, 0.12);
export const RAPID_ROBOT_NOTICE_BACKGROUND_DARK = BPS_BLUE;
export const RAPID_ROBOT_NOTICE_TEXT_LIGHT = BPS_BLUE;
export const RAPID_ROBOT_NOTICE_TEXT_DARK = WHITE;
export const RAPID_ROBOT_CREATE_BUTTON_BACKGROUND_LIGHT = BPS_BLUE;
export const RAPID_ROBOT_CREATE_BUTTON_BACKGROUND_DARK = WHITE;
export const RAPID_ROBOT_CREATE_BUTTON_HOVER_BACKGROUND_LIGHT = BPS_DARK_BLUE;
export const RAPID_ROBOT_CREATE_BUTTON_HOVER_BACKGROUND_DARK = BPS_LIGHT_GREY;
export const RAPID_ROBOT_CREATE_BUTTON_TEXT_LIGHT = WHITE;
export const RAPID_ROBOT_CREATE_BUTTON_TEXT_DARK = BPS_BLUE;

// Legacy Color Exports (for backward compatibility)
export const YELLOW = CHART_YELLOW;
export const PURPLE = CHART_PURPLE;
